// Controlador para Catálogo de Socios de Negocio
// Siguiendo MODO1: cat_socionegocio_controller
import axios from 'axios';
import { getApiUrl } from '@/config/api';
import authService from '@/shared/services/auth';

export default class CatSocioNegocioService {
  constructor() {
    this.sociosNegocio = [];
    this.loading = false;
    this.error = null;
    this.selectedEmpresa = null;
  }

  // Inicializar el servicio
  async initialize() {
    // Esperar un poco para que la empresa se seleccione
    await this.waitForEmpresa();
    await this.listar();
  }

  // Esperar a que la empresa esté seleccionada
  async waitForEmpresa() {
    let attempts = 0;
    const maxAttempts = 50; // 5 segundos máximo
    
    while (attempts < maxAttempts) {
      this.selectedEmpresa = authService.getSelectedEmpresa();
      if (this.selectedEmpresa && this.selectedEmpresa.EmpresaId) {
        console.log('Empresa encontrada para CatSocioNegocio:', this.selectedEmpresa);
        return;
      }
      
      // Esperar 100ms antes del siguiente intento
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    // Si no se encuentra empresa después de los intentos
    this.error = 'No se pudo obtener la empresa seleccionada';
    console.error('No se pudo obtener empresa para CatSocioNegocio después de', maxAttempts, 'intentos');
  }

  // Listar socios de negocio
  async listar() {
    this.loading = true;
    this.error = null;
    
    try {
      // Verificar que tenemos empresa seleccionada
      if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
        this.selectedEmpresa = authService.getSelectedEmpresa();
        if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
          this.loading = false;
          return { success: false, data: [], message: 'Debe elegir una empresa antes de poder continuar.' };
        }
      }

      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      console.log('Cargando socios de negocio para empresa:', this.selectedEmpresa.EmpresaId);

      const response = await axios.post(getApiUrl('/api/CatSocioNegocio/listar'), {
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      
      // Filtrar por empresa en el frontend como medida de seguridad adicional
      const allSociosNegocio = response.data || [];
      this.sociosNegocio = allSociosNegocio.filter(sociosNegoci => sociosNegoci.EmpresaId === this.selectedEmpresa.EmpresaId);
      this.loading = false;
      
      console.log('SociosNegocio cargados:', this.sociosNegocio.length, 'de', allSociosNegocio.length, 'total');
      console.log('Empresa del usuario:', this.selectedEmpresa.EmpresaId);
      this.loading = false;
      
      console.log('Socios de negocio cargados:', this.sociosNegocio.length);
      
      return {
        success: true,
        data: this.sociosNegocio,
        message: 'Socios de negocio cargados exitosamente'
      };
    } catch (error) {
      console.error('Error al cargar socios de negocio:', error);
      this.error = error.message;
      this.sociosNegocio = [];
      this.loading = false;
      throw error;
    }
  }

  // Guardar socio de negocio (insert/update)
  async guardar(socioNegocio) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      // Asegurar que tenga la empresa seleccionada
      socioNegocio.EmpresaId = this.selectedEmpresa.EmpresaId;

      const response = await axios.post(getApiUrl('/api/CatSocioNegocio/guardar'), socioNegocio, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Recargar la lista después de guardar
      await this.listar();
      
      return {
        success: true,
        data: response.data,
        message: socioNegocio.Clave ? 'Socio de negocio actualizado exitosamente' : 'Socio de negocio creado exitosamente'
      };
    } catch (error) {
      console.error('Error al guardar socio de negocio:', error);
      throw error;
    }
  }

  // Borrar socio de negocio
  async borrar(socioNegocio) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await axios.post(getApiUrl('/api/CatSocioNegocio/borrar'), socioNegocio, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Recargar la lista después de eliminar
      await this.listar();
      
      return {
        success: true,
        data: response.data,
        message: 'Socio de negocio eliminado exitosamente'
      };
    } catch (error) {
      console.error('Error al eliminar socio de negocio:', error);
      throw error;
    }
  }

  // Leer un socio de negocio específico
  async lectura(clave) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await axios.post(getApiUrl('/api/CatSocioNegocio/lectura'), {
        Clave: clave,
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return {
        success: true,
        data: response.data,
        message: 'Socio de negocio encontrado'
      };
    } catch (error) {
      console.error('Error al leer socio de negocio:', error);
      throw error;
    }
  }

  // Recargar socios de negocio
  async recargar() {
    await this.listar();
  }

  // Descarga masiva
  async downloadBulk() {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await axios.post(getApiUrl('/api/CatSocioNegocio/descargar'), {
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Crear blob y descargar
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `socios_negocio_${this.selectedEmpresa.Nombre}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error al descargar socios de negocio:', error);
      throw error;
    }
  }

  // Método para obtener configuración de columnas
  getDataGridColumns() {
    return [
      { key: 'Clave', label: 'Clave', sortable: true, required: true },
      { key: 'Nombre', label: 'Nombre', sortable: true, required: true },
      { key: 'Clave2', label: 'Clave 2', sortable: true },
      { key: 'ClaveErp', label: 'Clave ERP', sortable: true },
      { 
        key: 'EsActivo', 
        label: 'Activo', 
        sortable: true,
        format: (value) => value ? 'Sí' : 'No'
      }
    ];
  }

  // Método para obtener campos del formulario
  getFormFields() {
    return [
      {
        name: 'Clave',
        label: 'Clave',
        type: 'text',
        required: true,
        maxlength: 50
      },
      {
        name: 'Nombre',
        label: 'Nombre',
        type: 'text',
        required: true,
        maxlength: 100
      },
      {
        name: 'Clave2',
        label: 'Clave 2',
        type: 'text',
        maxlength: 50
      },
      {
        name: 'ClaveErp',
        label: 'Clave ERP',
        type: 'text',
        maxlength: 50
      },
      {
        name: 'EsActivo',
        label: 'Activo',
        type: 'checkbox',
        default: true
      }
    ];
  }
}
